import { useDispatch } from 'react-redux';
import {
	closestCenter,
	DndContext,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { memo, useCallback } from 'react';

import KanbanColumn from '@features/project-management-tools/components/KanbanColumn';
import {
	setCompletedProjects,
	setInProgressProjects,
	setNotStartedProjects
} from '@features/project-management-tools/redux/myProjectSlice';
import { useDragAndDropUpdate } from '@features/project-management-tools/hooks/useDragAndDropUpdate';
import SortableProjectCard from '@features/project-management-tools/components/SortableProjectCard';
import { MIX_PANEL_EVENTS } from '@features/project-management-tools/constents';

import { useMixPanelTrakingEventMutation } from '@/api/commonApis';
import { useMyProject } from '@/store/reducers';

const KanbanSection = memo(
	({
		title,
		count,
		className,
		items,
		itemIds,
		sensors,
		handleDragEnd,
		statusColor,
		onTrackMixPanelEvent
	}) => {
		return (
			<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
				<KanbanColumn title={title} count={count} className={className} statusColor={statusColor}>
					<SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
						{items &&
							items.map((project) => (
								<SortableProjectCard
									key={project?.id}
									id={project?.id}
									projectInfo={project}
									onTrackMixPanelEvent={onTrackMixPanelEvent}
								/>
							))}
					</SortableContext>
				</KanbanColumn>
			</DndContext>
		);
	}
);

const MyProjectKanbanBoard = () => {
	const dispatch = useDispatch();
	const { notStartedProjects, inProgressProjects, completedProjects } = useMyProject();
	const [mixPanelTrakingEvent] = useMixPanelTrakingEventMutation();

	// Set up DnD sensors
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8,
				delay: 200,
				tolerance: 8
			}
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates
		})
	);

	// Memoize dispatch callbacks
	const dispatchNotStarted = useCallback((data) => dispatch(setNotStartedProjects(data)), [dispatch]);
	const dispatchInProgress = useCallback((data) => dispatch(setInProgressProjects(data)), [dispatch]);
	const dispatchCompleted = useCallback((data) => dispatch(setCompletedProjects(data)), [dispatch]);

	// Use the drag and drop hook for each column
	// Not started
	const {
		handleDragEnd: handleDragEndNotStarted,
		sortedItems: sortedNotStarted,
		itemIds: notStartedIds
	} = useDragAndDropUpdate(notStartedProjects, dispatchNotStarted);

	// Running
	const {
		handleDragEnd: handleDragEndRunning,
		sortedItems: sortedInProgress,
		itemIds: inProgressIds
	} = useDragAndDropUpdate(inProgressProjects, dispatchInProgress);

	// Completed
	const {
		handleDragEnd: handleDragEndCompleted,
		sortedItems: sortedCompleted,
		itemIds: completedIds
	} = useDragAndDropUpdate(completedProjects, dispatchCompleted);

	const handleTrackMixPanelEvent = (projectId, projectName) => {
		mixPanelTrakingEvent({
			type: MIX_PANEL_EVENTS.PROJECT_LIST_ON_CLICK,
			body: {
				projectId,
				projectName
			}
		});
	};

	return (
		<>
			<KanbanSection
				title="Not started"
				statusColor="#9aa1af"
				count={sortedNotStarted.length || 0}
				className="task-not-started"
				items={sortedNotStarted}
				itemIds={notStartedIds}
				sensors={sensors}
				handleDragEnd={handleDragEndNotStarted}
				onTrackMixPanelEvent={handleTrackMixPanelEvent}
			/>

			<KanbanSection
				title="Running"
				statusColor="#ffa500"
				count={sortedInProgress.length || 0}
				className="task-running"
				items={sortedInProgress}
				itemIds={inProgressIds}
				sensors={sensors}
				handleDragEnd={handleDragEndRunning}
				onTrackMixPanelEvent={handleTrackMixPanelEvent}
			/>

			<KanbanSection
				title="Completed"
				statusColor="#8bc48a"
				count={sortedCompleted.length || 0}
				className="task-complete"
				items={sortedCompleted}
				itemIds={completedIds}
				sensors={sensors}
				handleDragEnd={handleDragEndCompleted}
				onTrackMixPanelEvent={handleTrackMixPanelEvent}
			/>
		</>
	);
};

export default memo(MyProjectKanbanBoard);
